import { useState, useEffect } from "react";
import { Button, Card, Row, Spin, Typography } from "antd";
import CareHiresLogo from "../../assets/img/CH_New_Logo.png";
import { showToast } from "../Toaster/Toaster";
import { reviewDevice, confirmThisWasMe, confirmNotMe } from "../../services/security.service";
import type { DeviceReviewInfo } from "../../types/security";

const { Title, Text, Link } = Typography;

type ReviewState = "loading" | "ready" | "error" | "confirmed-me" | "confirmed-not-me";

const DeviceReview: React.FC = () => {
  const [state, setState] = useState<ReviewState>("loading");
  const [device, setDevice] = useState<DeviceReviewInfo | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const token = new URLSearchParams(window.location.search).get("token") || "";

  useEffect(() => {
    if (!token) {
      setState("error");
      return;
    }
    reviewDevice(token)
      .then((res) => {
        if (res.success && res.data) {
          setDevice(res.data);
          setState("ready");
        } else {
          setState("error");
        }
      })
      .catch(() => setState("error"));
  }, []);

  const handleThisWasMe = async () => {
    setActionLoading(true);
    try {
      await confirmThisWasMe(token);
      setState("confirmed-me");
    } catch {
      showToast("Something went wrong. Please try again.", { type: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleNotMe = async () => {
    setActionLoading(true);
    try {
      await confirmNotMe(token);
      setState("confirmed-not-me");
    } catch {
      showToast("Something went wrong. Please try again.", { type: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <img className="forgot-password-logo" src={CareHiresLogo} alt="CareHires Logo" />
      <Row justify="center" align="middle">
        <Card className="forgot-password-card" style={{ maxWidth: 520, width: "100%" }}>
          {state === "loading" && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin size="large" />
              <p style={{ marginTop: 16, color: "#555" }}>Verifying your request…</p>
            </div>
          )}

          {state === "error" && (
            <>
              <Title level={3} style={{ color: "#121451" }}>Invalid or Expired Link</Title>
              <Text>This device review link is invalid or has already been used. Please sign in again if you need to verify a new device.</Text>
            </>
          )}

          {state === "ready" && device && (
            <>
              <Title level={3} style={{ color: "#121451" }}>New Sign-in Detected</Title>
              <Text>We noticed a sign-in from a new device. Was this you?</Text>
              <div style={{ margin: "20px 0", textAlign: "left", background: "#f5f5f5", borderRadius: 8, padding: "16px 20px" }}>
                <p style={{ margin: "4px 0" }}><strong>Browser:</strong> {device.browser}</p>
                <p style={{ margin: "4px 0" }}><strong>OS:</strong> {device.os}</p>
                <p style={{ margin: "4px 0" }}><strong>Device type:</strong> {device.deviceType}</p>
                <p style={{ margin: "4px 0" }}><strong>Location:</strong> {device.location}</p>
                <p style={{ margin: "4px 0" }}><strong>First seen:</strong> {device.firstSeen}</p>
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  type="primary"
                  loading={actionLoading}
                  onClick={handleThisWasMe}
                  style={{ minWidth: 140 }}
                >
                  Yes, this was me
                </Button>
                <Button
                  danger
                  loading={actionLoading}
                  onClick={handleNotMe}
                  style={{ minWidth: 140 }}
                >
                  No, this wasn&apos;t me
                </Button>
              </div>
            </>
          )}

          {state === "confirmed-me" && (
            <>
              <Title level={3} style={{ color: "#52c41a" }}>Device Verified</Title>
              <Text>This device has been marked as trusted. You can close this page.</Text>
            </>
          )}

          {state === "confirmed-not-me" && (
            <>
              <Title level={3} style={{ color: "#121451" }}>Account Secured</Title>
              <Text>
                Your account has been secured. All active sessions have been revoked.
                If you did not perform this action, please{" "}
                <Link
                  href={import.meta.env.VITE_SUPPORT_DESK_URL || "https://ask.carehires.com/en/knowledge/kb-tickets/new"}
                  target="_blank"
                  rel="noreferrer"
                >
                  contact support
                </Link>
                .
              </Text>
            </>
          )}
        </Card>
      </Row>
    </div>
  );
};

export default DeviceReview;
